import { MICROGRID_STATUS } from 'features/microgrid/microgrid.constants';
import { ReactNode } from 'react';
import { Edge as FlowEdge, Node as FlowNode } from 'reactflow';

export type MicrogridNodeData =
  | BatteryData
  | GridConnectionData
  | GeneratorData
  | EnergyMeterData
  | LoadData
  | DistributionBoxData;

type MicrogridNodeDataWithMeta = MicrogridNodeData & {
  icon?: string;
  isNew?: boolean;
};

export type MicrogridEdgeData = {
  organisation?: { name: string };
  isNew?: boolean;
  sourceId?: string;
  sourceConnectionId?: string;
  targetId?: string;
  targetConnectionId?: string;
  meta?: object;
  label?: string | ReactNode | null;
};

export type CustomNode = FlowNode<MicrogridNodeDataWithMeta> & {
  isNew?: boolean;
  label?: string;
  meta?: object;
};

export type CustomEdge = FlowEdge & MicrogridEdgeData;

export type MutateMicrogridSetupResult = {
  nodes: CustomNode[];
  edges: CustomEdge[];
};

export type StatusVariant = keyof typeof MICROGRID_STATUS;

export type Microgrid = {
  projectId: number;
  name: string;
  description: string;
  createdByUser: {
    username: string;
  } | null;
  tsStart: Date;
  tsEnd?: Date;
  tsCreated: string;
  tsLastModified: string;
  label?: string;
  microgridId: string;
  nodes?: Array<CustomNode>;
  edges?: Array<CustomEdge>;
  status?: StatusVariant;
  visibleToClients: boolean;
};

export type MicrogridCreate = {
  name: Microgrid['name'];
  description: Microgrid['description'];
  tsStart: Microgrid['tsStart'];
  tsEnd?: Microgrid['tsEnd'];
  label?: Microgrid['label'];
};

enum ConnectionFitType {
  '_0' = 0,
  '_1' = 1,
}

enum ConnectorType {
  EE_1P = 'ee-1p',
  CEE_3P = 'cee-3p',
  POWERLOCK = 'powerlock',
  CABLE_LUG = 'cable_lug',
  SCREW_TERMINAL = 'screw_terminal',
}

export type Connection = {
  id: string;
  rated_current?: number;
  /**
   *  0: hole, 1: pin
   */
  connection_fit?: ConnectionFitType;
  connector_type?: ConnectorType;
};

export type LoadData = {
  externalId: string;
  greenerUnitExtId?: string;
  /**
   * In amps
   */
  ratedCurrent?: number;
  /**
   * In kW
   */
  ratedActivePower?: number;
  label: string;
  connections: Array<Connection>;
};

export enum NodeTypes {
  LOAD = 'LOAD',
  BATTERY = 'BATTERY',
  GRID = 'GRID',
  ENERGY_METER = 'ENERGY_METER',
  DIESEL_GENSET = 'DIESEL_GENSET',
  DISTRIBUTION_BOX = 'DISTRIBUTION_BOX',
  ANONYMOUS_NODE = 'ANONYMOUS_NODE',
}

export type BatteryData = {
  externalId: string;
  greenerUnitExtId?: string;
  /**
   * In kWh
   */
  storageCapacity?: number;
  /**
   * In kVA
   */
  ratedApparentPower?: number;
  /**
   * In kW
   */
  ratedActivePower?: number;
  label: string;
  connections: Array<Connection>;
  /**
   * Set to True if this battery is in the lead.
   */
  isPrimary?: boolean;
  isBatterySwap?: boolean;
  controlledByGreenerUnitExtId?: string;
};

export type AnonymousData = {
  label: string;
  description?: string;
};

export type DistributionBoxData = {
  externalId?: string;
  greenerUnitExtId?: string;
  /**
   * In amps
   */
  ratedCurrent?: number;
  label: string;
  connections: Array<Connection>;
};

export type GridConnectionData = {
  externalId: string;
  label: string;
  /**
   * In kVA
   */
  ratedApparentPower?: number;
  /**
   * In amps
   */
  ratedCurrent?: number;
  /**
   * In kW, maximum peak power allowed by the grid operator
   */
  contractedActivePower?: number;
  greenerUnitExtId?: string;
  connections: Array<Connection>;
};
export type GeneratorData = {
  externalId: string;
  greenerUnitExtId?: string;
  /**
   * In kVA
   */
  ratedApparentPower?: number;
  /**
   * In kW
   */
  ratedActivePower?: number;
  relayId?: number;
  activePowerSetting?: number;
  controlledByGreenerUnitExtId?: string;
  label: string;
  connections: Array<Connection>;
};
export type EnergyMeterData = {
  externalId: string;
  greenerUnitExtId?: string;
  /**
   * In amps
   */
  ratedCurrent?: number;
  label: string;
  connections: Array<Connection>;
};

export type MicrogridCompatibilityErrors = {
  greener_unit_not_registered: string[];
  greener_unit_incompatible_start_date: string[];
  greener_unit_incompatible_end_date: string[];
};

export type MicrogridEdgeErrors = {
  connections_with_multiple_edges: string[];
  edge_id_not_unique: string[];
  edges_with_invalid_connections: string[];
  edges_with_invalid_nodes: string[];
};

export type MicrogridNodeErrors = {
  microgrid_genset_node_misses_rated_apparent_power: string[];
  microgrid_greener_unit_ext_id_not_unique: string[];
  microgrid_grid_node_misses_rated_current: string[];
  microgrid_has_no_nodes: string[];
  microgrid_has_anonymous_nodes: string[];
  microgrid_node_misses_greener_unit_ext_id: string[];
  microgrid_nodes_not_unique: string[];
  microgrid_nodes_without_edges: string[];
  microgrid_genset_node_misses_relay_id: string[];
  microgrid_genset_node_misses_controlled_by_greener_unit_ext_id: string[];
  microgrid_genset_node_controlled_by_greener_unit_not_in_microgrid: string[];
  microgrid_genset_node_misses_active_power_setting: string[];
  microgrid_battery_with_second_inverter_has_no_unit_with_second_inverter: string[];
};

export type ProjectMicrogridCompatibilityErrors = {
  overlapping_valid_microgrids: string[];
  incompatible_project_start_date: string[];
  incompatible_project_end_date: string[];
};

export type ProjectMicrogridErrors = {
  project_microgrid_invalid_dates: string;
};

export type ConverterErrors = string[];

export type ProcessedErrorObject = {
  field: string;
  errorMessage: string;
  ids: string[];
  source: string;
  target: string;
};

export interface ErrorObject {
  microgrid_compatibility_errors: MicrogridCompatibilityErrors;
  microgrid_edge_errors: MicrogridEdgeErrors;
  microgrid_node_errors: MicrogridNodeErrors;
  project_microgrid_compatibility_errors: ProjectMicrogridCompatibilityErrors;
  project_microgrid_errors: ProjectMicrogridErrors;
  converter_errors: ConverterErrors;
}

export type UpdateNodeData = {
  externalId?: string;
  label?: string;
  connections?: Array<Connection>;
  greenerUnitExtId?: string;
  /**
   * In kWh
   */
  storageCapacity?: number;
  /**
   * In amps
   */
  ratedCurrent?: number;
  /**
   * In kVA
   */
  ratedApparentPower?: number;
  /**
   * In kW
   */
  ratedActivePower?: number;
  /**
   * In kW
   */
  relayId?: number;
  activePowerSetting?: number;
  controlledByGreenerUnitExtId?: string;
  connectionsNumber: number;
  isPrimary?: boolean;
};

export type ErrorDetail = { response: { data: { detail: ErrorObject } } };
